import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Mail, MailOpen, Reply, Loader2 } from "lucide-react";

export const AdminMessages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/contact/messages")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const markRead = async (m) => {
    if (m.read) return;
    try {
      await api.post(`/contact/messages/${m.id}/read`);
      setItems((xs) => xs.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
    } catch (e) {
      toast.error("Impossible de marquer comme lu");
    }
  };

  const unread = items.filter((m) => !m.read).length;

  return (
    <div className="space-y-6" data-testid="admin-messages-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)]">Rédaction</div>
          <h1 className="font-display text-3xl md:text-4xl mt-1">Messages du formulaire</h1>
          <p className="text-sm text-neutral-500 mt-2">
            {items.length} message{items.length > 1 ? "s" : ""} au total ·{" "}
            <span className="text-[var(--red)] font-medium">{unread}</span> non lu
            {unread > 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="btn-ghost text-xs"
          data-testid="admin-messages-refresh"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Actualiser"}
        </button>
      </div>

      {loading && items.length === 0 ? (
        <div className="text-neutral-500 text-sm">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-[var(--line)] bg-white p-10 text-center text-neutral-500">
          Aucun message pour l'instant.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
          {/* LIST */}
          <ul
            className="rounded-lg border border-[var(--line)] bg-white divide-y divide-[var(--line)] overflow-hidden max-h-[75vh] overflow-y-auto"
            data-testid="admin-messages-list"
          >
            {items.map((m) => (
              <li
                key={m.id}
                onClick={() => {
                  setSelected(m);
                  markRead(m);
                }}
                className={`px-4 py-3 cursor-pointer transition-colors ${
                  selected?.id === m.id
                    ? "bg-[var(--red-soft)]"
                    : "hover:bg-[var(--bg-cream)]"
                }`}
                data-testid={`admin-message-row-${m.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {m.read ? (
                    <MailOpen className="h-3.5 w-3.5 text-neutral-400" />
                  ) : (
                    <Mail className="h-3.5 w-3.5 text-[var(--red)]" />
                  )}
                  <span className={`text-sm truncate flex-1 ${m.read ? "text-neutral-600" : "font-medium"}`}>
                    {m.name}
                  </span>
                  <span className="text-[10px] text-neutral-400 shrink-0">
                    {formatDate(m.created_at)}
                  </span>
                </div>
                <div className="text-xs text-neutral-500 truncate pl-5">{m.subject}</div>
              </li>
            ))}
          </ul>

          {/* DETAIL */}
          <div className="rounded-lg border border-[var(--line)] bg-white p-6 md:p-8 min-h-[300px]" data-testid="admin-message-detail">
            {!selected ? (
              <div className="text-neutral-400 text-sm h-full grid place-items-center min-h-[240px]">
                Sélectionne un message pour l'afficher.
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-4 pb-4 border-b border-[var(--line)] mb-4">
                  <div className="min-w-0">
                    <h2 className="font-display text-2xl truncate">{selected.subject}</h2>
                    <div className="text-sm text-neutral-600 mt-1">
                      De <span className="text-neutral-900">{selected.name}</span> —{" "}
                      <a
                        href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                        className="text-[var(--red)] hover:underline"
                      >
                        {selected.email}
                      </a>
                    </div>
                    <div className="text-xs text-neutral-400 mt-1">
                      Reçu le {formatFullDate(selected.created_at)}
                    </div>
                  </div>
                  <a
                    href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                    className="btn-red text-xs shrink-0"
                    data-testid="admin-message-reply"
                  >
                    <Reply className="h-3.5 w-3.5" /> Répondre
                  </a>
                </div>
                <div className="whitespace-pre-wrap text-sm text-neutral-800 leading-relaxed">
                  {selected.message}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
};

const formatFullDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};
